import { useState } from "react";
import { Icon } from "@shopflo/ui/icon";
import { FullCardCarousel } from "@shopflo/ui/full-card-carousel";
import { CaretLeft, Minus, Plus, SealPercent } from "@phosphor-icons/react";
import { useDiscountAchieverContext } from "lib/contexts/DiscountAchieverProvider";
import { getInventoryCap } from "lib/types/discountAchiever";
import { currencyFormatter } from "lib/utils/formatters";
const DISCOUNT_GRADIENT_ID = "discountOffGradient";
const DISCOUNT_GRADIENT_CSS = "linear-gradient(270deg, #2C874A 46.85%, #0B2112 108.98%, #2C874A 109.96%)";
/** Off-screen paint server so the SealPercent icon can fill with the same gradient as the text */
const DiscountGradientDef = () => (
  <svg aria-hidden width="0" height="0" className="absolute">
    <defs>
      {/* x1=1 x2=0 mirrors the CSS 270deg (right → left) */}
      <linearGradient id={DISCOUNT_GRADIENT_ID} x1="1" y1="0" x2="0" y2="0">
        <stop offset="46.85%" stopColor="#2C874A" />
        <stop offset="100%" stopColor="#0B2112" />
      </linearGradient>
    </defs>
  </svg>
);

interface ProductViewerStepProps {
  onBack: () => void;
  onAdd: () => void;
  /** Route to the variant selector tray view (third view) */
  onOpenVariants: () => void;
}

export const ProductViewerStep = ({ onBack, onAdd, onOpenVariants }: ProductViewerStepProps) => {
  const [qty, setQty] = useState(1);
  const { state, actions } = useDiscountAchieverContext();
  const product = state.activeDetailProduct;

  const variant = product?.variants[0];
  const format = (amount: number) =>
    currencyFormatter(amount, true, { locale: "en-IN", currency: variant?.currency || "INR" });
  const price = variant ? (variant.discounted_price ?? variant.current_price) : 0;
  const compareAtPrice = variant && variant.original_price > price ? variant.original_price : undefined;
  const discountPercentage = compareAtPrice
    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
    : undefined;
  const images = (product?.images ?? []).map((image) => image.src).filter(Boolean);
  const moreOptionsCount = (product?.variants.length ?? 0) - 1;
  // Stepper adds on top of the current selection; cap the + at remaining stock (provider re-clamps)
  const alreadySelected = variant ? (state.blocks[state.activeBlockIndex]?.selectedItems[variant.variant_id] ?? 0) : 0;
  const remainingStock = variant ? Math.max(0, getInventoryCap(variant) - alreadySelected) : Infinity;
  const isAtMax = qty >= remainingStock;
  const handleAdd = () => {
    if (variant) actions.updateQty(state.activeBlockIndex, variant.variant_id, qty);
    onAdd();
  };

  return (
    <div className="relative flex h-[var(--tray-h-product-details,500px)] flex-col bg-white">
      {/* Header — image carousel, pinned */}
      <div className="relative shrink-0">
        <FullCardCarousel images={images} edgeToEdge />
        <button
          type="button"
          onClick={onBack}
          className="absolute left-4 top-4 z-30 rounded-[10px] bg-black/[0.24] p-1.5">
          <Icon IconComponent={CaretLeft} size="md" iconProps={{ weight: "bold", color: "white" }} />
        </button>
      </div>

      {/* Body — scrolls between header and footer */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto scrollbar-hide">
        <div className="flex shrink-0 items-start gap-4 px-6 pb-3 pt-6">
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-text-md-semibold text-ink-strong">
              {product?.name ?? ""}
            </p>
            <p className="text-text-sm-medium text-ink-strong opacity-60">{variant?.name ?? "60ml"}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {discountPercentage !== undefined && (
              <div className="flex items-center gap-1 rounded-md px-1">
                <DiscountGradientDef />
                <Icon
                  IconComponent={SealPercent}
                  size="sm"
                  iconProps={{ weight: "fill", color: `url(#${DISCOUNT_GRADIENT_ID})` }}
                />
                <span
                  className="text-text-xs-bold"
                  style={{
                    backgroundImage: DISCOUNT_GRADIENT_CSS,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}>
                  {discountPercentage}% off
                </span>              </div>
            )}
            <div className="flex items-center gap-1">
              {compareAtPrice !== undefined && (
                <span className="text-text-xs-medium text-ink-strong opacity-60 line-through">
                  {format(compareAtPrice)}
                </span>
              )}
              <span className="text-text-lg-bold text-ink-strong">{format(price)}</span>
            </div>
          </div>
        </div>
        {moreOptionsCount > 0 && (
          <div className="px-6 pb-2">
            <button
              type="button"
              onClick={onOpenVariants}
              className="w-full rounded-md bg-white py-2 text-text-sm-semibold text-primary-dark"
              style={{
                boxShadow:
                  "0 0 0 1.5px color-mix(in srgb, var(--flo-primary-dark-color) 40%, transparent), 0 1px 2px 0 rgba(10, 13, 18, 0.05)",
              }}>
              +{moreOptionsCount} more options
            </button>
          </div>
        )}
        <p className="px-6 text-text-xs-regular text-ink-strong opacity-60">
          {product?.description || "No description available"}
        </p>
        <div className="h-3 w-full shrink-0 border-t border-gray-100" />
        
      </div>

      {/* Footer — qty stepper + CTA, pinned */}
      <div
        className="z-10 flex shrink-0 gap-2 bg-white px-4 pb-4 pt-3"
        style={{ boxShadow: "0px -4px 16px rgba(0, 0, 0, 0.06)" }}>
        <div className="flex h-[48px] min-w-[115px] items-center justify-between rounded-xl bg-primary-lighter">
          <button
            type="button"
            disabled={qty <= 1}
            onClick={() => setQty((count) => Math.max(1, count - 1))}
            // rounded-l-xl so the hover fill follows the pill's left corners — the parent isn't
            // overflow-hidden, so a square hover background would escape it
            className="flex items-center justify-center rounded-l-xl p-3 hover:bg-shade-4 disabled:opacity-40 disabled:hover:bg-transparent">
            <Icon
              IconComponent={Minus}
              size="md"
              iconProps={{ weight: "bold", color: "var(--flo-primary-dark-color)" }}
            />
          </button>
          <span className="text-text-md-semibold text-primary-dark text-center min-w-8">{qty}</span>
          <button
            type="button"
            disabled={isAtMax}
            onClick={() => setQty((count) => count + 1)}
            className="flex items-center justify-center rounded-r-xl p-3 hover:bg-shade-4 disabled:opacity-40 disabled:hover:bg-transparent">
            <Icon
              IconComponent={Plus}
              size="md"
              iconProps={{ weight: "bold", color: "var(--flo-primary-dark-color)" }}
            />
          </button>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          // Out of stock (or no variant) means updateQty would no-op — block the CTA so the tray
          // doesn't navigate back as if the item was added
          disabled={!variant || remainingStock === 0}
          className="flex-1 rounded-xl bg-primary-dark text-text-md-semibold text-btnPrimaryText transition-transform active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100">
          Add Selected
        </button>
      </div>
    </div>
  );
};
